import {
  AssociationItem,
  EditorialPerInitiative,
} from "../../AddActivity/types";
import { SelectedLOItem } from "../../types";
import {
  mapLOChildrenById,
  mapLOStructureToSelectedItems,
} from "../../utils/loStructure";
import { isNoObsolescenceDate } from "../../utils/obsolescence";
import {
  CompletionCriterion,
  ComplexityLevel,
  PropaedeuticLogic,
  TestLogic,
} from "../../Course/types";
import { EMPTY_PATH_DATA, PathWizardData } from "../types";

const SENTINEL_FAR_FUTURE = 253402214400000;

const toIsoOrNull = (d: string | number | null | undefined): string | null => {
  if (d == null) return null;
  if (typeof d === "number") {
    if (d === SENTINEL_FAR_FUTURE) return null;
    return new Date(d).toISOString();
  }
  return d;
};

const toAssociationItem = (
  v: { id: number | string; label?: string; name?: string } | null | undefined,
): AssociationItem | null => {
  if (!v || v.id == null) return null;
  return { id: Number(v.id), label: v.label || v.name || `#${v.id}` };
};

const toAssociationList = (
  list:
    | Array<{ id: number | string; label?: string; name?: string }>
    | null
    | undefined,
): AssociationItem[] => {
  if (!list || !Array.isArray(list)) return [];
  return list
    .map((it) => toAssociationItem(it))
    .filter((x): x is AssociationItem => x !== null);
};

const numToString = (n: number | string | null | undefined): string => {
  if (n == null) return "";
  return String(n);
};

const detectCompletionCriterion = (
  detail: Record<string, any>,
): { criterion: CompletionCriterion | null; value: string } => {
  if (detail.completion_criteria_lo_required === true) {
    return { criterion: "LO_REQUIRED", value: "" };
  }

  const isMeaningful = (v: any): boolean => v != null && Number(v) > 0;

  if (isMeaningful(detail.lo_to_complete)) {
    return {
      criterion: "LO_TO_COMPLETE",
      value: numToString(detail.lo_to_complete),
    };
  }
  if (isMeaningful(detail.min_completition_perc)) {
    return {
      criterion: "MIN_COMPLETION_PERC",
      value: numToString(detail.min_completition_perc),
    };
  }
  if (isMeaningful(detail.completition_minutes)) {
    return {
      criterion: "COMPLETION_MINUTES",
      value: numToString(detail.completition_minutes),
    };
  }
  if (isMeaningful(detail.completition_minutes_perc)) {
    return {
      criterion: "COMPLETION_MINUTES_PERC",
      value: numToString(detail.completition_minutes_perc),
    };
  }
  return { criterion: null, value: "" };
};

const detectKind = (item: {
  type?: string;
  learning_object_type?: string;
}): "activity" | "course" => {
  const t = (item.type || "").toUpperCase();
  if (t === "COURSE") return "course";
  if (t.startsWith("DA_")) return "activity";
  const lot = (item.learning_object_type || "").toUpperCase();
  if (lot === "COURSE") return "course";
  return "activity";
};

const toSelectedLOItem = (item: {
  id: number | string;
  title?: string;
  label?: string;
  name?: string;
  category?: string;
  type?: string;
  learning_object_type?: string;
  [k: string]: any;
}): SelectedLOItem => {
  const id = Number(item.id);
  const title = item.title || item.label || item.name || `#${id}`;
  const kind = detectKind(item);
  const type = item.category || item.learning_object_type || item.type || "";
  return {
    id,
    title,
    type,
    kind,
    entity: {
      ...item,
      id,
      title,
      type,
    },
    locked: true,
  };
};

export function mapPathDetailToWizardData(
  detail: Record<string, any>,
  editorialByInitiative: Record<number, EditorialPerInitiative> = {},
  children: readonly Record<string, any>[] = [],
): PathWizardData {
  const { criterion, value } = detectCompletionCriterion(detail);
  const publishDate = toIsoOrNull(detail.publish_date);
  const obsolescentDate = isNoObsolescenceDate(detail.obsolescent_date)
    ? null
    : toIsoOrNull(detail.obsolescent_date);

  const schedulePublication = !!publishDate;
  const childrenById = mapLOChildrenById(children);
  const selectedLOs = Array.isArray(detail.structure)
    ? mapLOStructureToSelectedItems(detail.structure, (id) =>
        toSelectedLOItem({ ...childrenById.get(id), id }),
      )
    : Array.isArray(detail.lo)
      ? detail.lo.map(toSelectedLOItem)
      : Array.isArray(detail.didactical_activity)
        ? detail.didactical_activity.map(toSelectedLOItem)
        : [];

  return {
    ...EMPTY_PATH_DATA,

    title: detail.title || "",
    digited_code: detail.digited_code || "",
    detail_description: detail.detail_description || "",
    allow_post_publish_additions: !detail.completable,
    schedule_publication: schedulePublication,
    publish_date: publishDate,
    schedule_obsolescence: !!obsolescentDate,
    obsolescent_date: obsolescentDate,
    propaedeutic_logics: (detail.propaedeutic_logics ||
      null) as PropaedeuticLogic | null,
    completion_criterion: criterion,
    completion_value: value,
    cover_id: toAssociationItem(detail.cover_id),
    video_id: toAssociationItem(detail.video_id),

    certificate_ids: toAssociationList(detail.certificate_ids),
    signature_certificate_ids: toAssociationList(
      detail.signature_certificate_ids,
    ),
    badge_id: toAssociationItem(detail.badge_id),
    complexity: (detail.complexity || null) as ComplexityLevel | null,
    score: numToString(detail.score),
    credits: numToString(detail.credits),
    lo_tag: Array.isArray(detail.lo_tag) ? detail.lo_tag : [],
    useful_links: Array.isArray(detail.useful_links) ? detail.useful_links : [],
    stakeholders: Array.isArray(detail.stakeholders) ? detail.stakeholders : [],
    time_slots: Array.isArray(detail.time_slots) ? detail.time_slots : [],
    didactical_material: toAssociationList(detail.didactical_material),

    selected_los: selectedLOs,

    test_id: toAssociationItem(detail.test_id),
    survey_id: toAssociationItem(detail.survey_id),
    test_logic: (detail.test_logic || null) as TestLogic | null,
    test_min_score: numToString(detail.test_min_score),
    max_attempt: numToString(detail.max_attempt),

    selected_initiatives: toAssociationList(detail.initiative),

    editorial_per_initiative: editorialByInitiative,
    highlight_explore: !!detail.highlight_explore,
  };
}
